import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { Helmet } from 'react-helmet-async';
import { getApiUrl } from '../../utils/apiConfig';
import LoginPrompt from '../../Auth/LoginPrompt';
import ProUpgradeModal from '../../components/chat/ProUpgradeModal';
// 🔍 파일 사전 검사
import FilePreCheckModal from '../../components/common/FilePreCheckModal';
import Dialog from '../../components/common/Dialog';
import ModalActions from '../../components/common/ModalActions';
import { preCheckFiles } from '../../api/chatApi';
// 📎 파일 첨부 한도 중앙 설정
import { getFileLimits, MAX_FILES_DEFAULT, MAX_PDF_PAGES, MAX_PPT_SLIDES } from '../../constants/fileLimits';
import { normalizePreCheckStats, summarizeFileStats } from '../../utils/filePreCheckStats';
import { useToast } from '../../components/Toast';
import './LabPage.css';

const LabPage = ({ defaultFeature = null }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { subscriptionStatus } = useSubscription();
  const isPro = subscriptionStatus?.isPro || false;
  
  const [selectedFeature, setSelectedFeature] = useState(defaultFeature);
  
  // URL 경로 변경 시 해당 기능으로 자동 이동 + 쿠폰 파라미터 감지
  useEffect(() => {
    const pathname = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    
    // 쿠폰 코드 파라미터 감지 (예: ?teacher30 또는 ?TEACHER30)
    // URL 파라미터 -> 실제 쿠폰 코드 매핑 (대소문자 무관하게 처리)
    const couponCodeMap = {
      'teacher30': 'TEACHER30',
      'TEACHER30': 'TEACHER30',
      'pro30': 'PRO30',
      'PRO30': 'PRO30',
      'free30': 'FREE30',
      'FREE30': 'FREE30',
    };
    
    let foundCoupon = null;
    
    // URL 파라미터에서 쿠폰 코드 찾기
    for (const [urlCode, actualCode] of Object.entries(couponCodeMap)) {
      if (searchParams.has(urlCode) || location.search.toLowerCase().includes(urlCode.toLowerCase())) {
        foundCoupon = actualCode; // 실제 쿠폰 코드 (대문자)
        break;
      }
    }
    
    // 쿠폰 코드가 감지되면 localStorage에 저장
    if (foundCoupon) {
      localStorage.setItem('pending_coupon_code', foundCoupon);
      // URL에서 쿠폰 파라미터 제거 (깔끔한 URL 유지)
      if (pathname === '/lab/image') {
        window.history.replaceState({}, '', '/lab/image');
      }
    }
    
    if (pathname === '/lab/image') {
      setSelectedFeature('imageGeneration');
    } else if (pathname === '/lab/evaluation') {
      // 서술형 채점은 별도 페이지로 이동
      navigate('/scoring');
      return;
    } else if (pathname === '/lab') {
      setSelectedFeature(null);
    }
  }, [location.pathname, location.search, navigate]);
  
  // 평가 기준 상태
  const [rubricText, setRubricText] = useState('');
  const [rubricFile, setRubricFile] = useState(null);
  const [rubricFileUploading, setRubricFileUploading] = useState(false);
  
  // 학생 답안 파일들
  const [answerFiles, setAnswerFiles] = useState([]);
  const [answerFilesStats, setAnswerFilesStats] = useState([]);  // 🆕 파일별 통계
  const [isDragging, setIsDragging] = useState(false);
  
  // 평가 결과
  const [evaluationResults, setEvaluationResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // 이미지 생성 상태
  const [imagePrompt, setImagePrompt] = useState('');
  const [imageCategory, setImageCategory] = useState('explanation'); // 용도
  const [generatedImage, setGeneratedImage] = useState(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);

  // 준비 중 모달 상태
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [comingSoonFeature, setComingSoonFeature] = useState(null);
  
  // 로그인/PRO 모달 상태
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showProRequiredModal, setShowProRequiredModal] = useState(false);
  
  // 🔍 파일 사전 검사 관련 상태
  const [showPreCheckModal, setShowPreCheckModal] = useState(false);
  const [preCheckResult, setPreCheckResult] = useState(null);
  const [pendingAnswerFiles, setPendingAnswerFiles] = useState(null);
  const [pendingRubricFile, setPendingRubricFile] = useState(null);
  const [preCheckLoading, setPreCheckLoading] = useState(false);
  
  // 🔧 함수 참조를 위한 ref (useCallback 순서 독립성 확보)
  const actualRubricFileUploadRef = useRef();
  const actualAnswerFilesUploadRef = useRef();
  
  // 권한 확인 함수 (비로그인 -> 로그인 모달, FREE -> PRO 모달)
  const checkAccess = useCallback(() => {
    if (!currentUser) {
      setShowLoginModal(true);
      return false;
    }
    if (!isPro) {
      setShowProRequiredModal(true);
      return false;
    }
    return true;
  }, [currentUser, isPro]);

  // 실험 기능 목록
  const FEATURES = {
    imageGeneration: {
      id: 'imageGeneration',
      title: '이미지 생성',
      subtitle: 'AI가 수업용 이미지를 생성합니다',
      color: '#10B981',
      available: true,
      isNew: true
    },
    pptGeneration: {
      id: 'pptGeneration',
      title: 'PPT 생성',
      subtitle: '주제만 입력하면 슬라이드 자동 생성',
      color: '#F59E0B',
      available: false,
      isNew: true
    }
  };

  // 카테고리 선택 핸들러
  const handleFeatureSelect = (featureId) => {
    const feature = FEATURES[featureId];
    
    // 준비 중인 기능인 경우
    if (!feature.available) {
      setComingSoonFeature(feature);
      setShowComingSoonModal(true);
      return;
    }
    
    // URL 경로로 이동
    if (featureId === 'imageGeneration') {
      navigate('/lab/image');
    } else {
      setSelectedFeature(featureId);
    }
  };

  // 뒤로가기
  const handleBackToSelection = () => {
    // URL로 직접 접근한 경우 /lab으로 이동
    if (defaultFeature) {
      navigate('/lab');
    } else {
      setSelectedFeature(null);
    }
    // 평가 상태 초기화
    setRubricText('');
    setRubricFile(null);
    setAnswerFiles([]);
    setAnswerFilesStats([]);  // 🆕 통계도 초기화
    setEvaluationResults([]);
    // 이미지 생성 상태 초기화
    setImagePrompt('');
    setImageCategory('explanation');
    setGeneratedImage(null);
    setReferenceImage(null);
    setReferenceImagePreview(null);
  };
  
  // 이미지 생성 용도 옵션 (각 용도별 시스템 프롬프트 포함)
  const IMAGE_CATEGORIES = [
    { 
      id: 'explanation', 
      label: '개념 설명용', 
      systemPrompt: `[이미지 용도] 대한민국 초중고 학교 교사가 수업 시간에 학생들에게 교과 개념을 설명할 때 사용하는 교육용 시각 자료
[사용 환경] 교실 TV, 프로젝터, 교과서, 참고 자료
[이미지 스타일 요구사항]
- 복잡한 개념을 단순하고 명확하게 시각화
- 단계별 화살표, 번호, 한글 라벨을 포함하여 순서와 관계를 명확히 표현
- 교과서나 학습 자료에 적합한 깔끔하고 전문적인 일러스트 스타일
- 밝고 선명한 색상으로 집중력 향상
- 불필요한 장식 요소 배제, 핵심 정보에 집중
- 학생들이 한눈에 이해할 수 있도록 직관적인 구성`
    },
    { 
      id: 'worksheet', 
      label: '학습지/활동지용', 
      systemPrompt: `[이미지 용도] 대한민국 학교 선생님이 학생들에게 나눠주는 학습지, 활동지, 프린트물에 삽입하는 교육용 삽화
[사용 환경] A4 프린트 출력, 흑백 또는 컬러 인쇄
[이미지 스타일 요구사항]
- 흑백 인쇄에도 명확히 보이도록 굵은 외곽선과 높은 명암 대비
- 학생들이 색칠하거나 빈칸을 채울 수 있는 여백 고려
- 귀엽고 친근한 캐릭터 스타일로 학습 동기 유발
- 단순하고 깔끔한 선화 스타일
- 교육적이면서도 재미있는 분위기
- 초등학생도 이해하기 쉬운 친절한 표현`
    },
    { 
      id: 'ppt', 
      label: 'PPT/발표용', 
      systemPrompt: `[이미지 용도] 대한민국 학교 교사가 수업 시간에 TV나 프로젝터로 보여줄 PPT, 파워포인트 발표 자료에 삽입하는 이미지
[사용 환경] 교실 대형 TV, 빔프로젝터, 전자칠판
[이미지 스타일 요구사항]
- 멀리서도 잘 보이도록 크고 선명한 요소들
- 텍스트는 최소화하고 시각적 임팩트에 집중
- 배경이 단순하거나 투명하여 슬라이드 배경과 자연스럽게 조화
- 현대적이고 세련된 플랫 디자인 또는 일러스트 스타일
- 고해상도로 확대해도 선명한 품질
- 수업 내용을 한눈에 전달하는 직관적 구성`
    },
    { 
      id: 'test', 
      label: '시험/평가용', 
      systemPrompt: `[이미지 용도] 대한민국 학교의 중간고사, 기말고사, 단원평가 시험지에 들어가는 교육용 도해 및 그림
[사용 환경] A4/B4 시험지 흑백 인쇄
[이미지 스타일 요구사항]
- 정확하고 객관적인 정보만 전달, 장식적 요소 완전 배제
- 흑백 인쇄 최적화: 명암 대비 극대화, 회색 음영 활용
- 문제 풀이에 필요한 정보만 담긴 간결한 도해
- 수치, 기호, 라벨이 명확히 읽히는 크기
- 과학, 수학, 사회 교과의 평가 문항에 적합한 정확한 표현
- 학생이 답을 작성할 수 있는 빈칸이나 표시 영역 고려`
    },
    { 
      id: 'poster', 
      label: '포스터/게시용', 
      systemPrompt: `[이미지 용도] 대한민국 초등학교, 중학교, 고등학교 교실 게시판, 복도 게시판에 붙일 교육용 포스터
[사용 환경] A3 이상 대형 컬러 인쇄, 교실 벽면 게시
[이미지 스타일 요구사항]
- 밝고 화려한 색상으로 학생들의 시선을 끄는 눈에 띄는 디자인
- 긍정적이고 교육적인 메시지 전달
- 멀리서도 눈에 띄는 큰 글씨와 명확한 타이포그래피
- 귀여운 캐릭터나 활기찬 일러스트 포함
- 학교 환경에 적합한 건전하고 밝은 분위기
- 독서, 안전, 예절, 환경 등 교육적 주제에 적합`
    },
    { 
      id: 'classPhoto', 
      label: '우리반 크레파스', 
      systemPrompt: `[이미지 용도] 사진을 10살 아이가 그린 크레파스 그림처럼 변환. 학급 추억, 가족, 친구 사진에 활용하는 따뜻한 기념물.

[변환 스타일]
- 전체 이미지를 한 장의 크레파스 손그림처럼 표현
- 디테일은 단순하게, 10살 아이가 그린 듯한 살짝 어설픈 외곽선
- 크레파스 특유의 왁시한 질감과 손그림 느낌

[색상 규칙]
- 원본 사진의 색상은 사용하지 말 것
- 부드러운 크레파스 파스텔 톤과 따뜻한 원색 조합
- 흰 종이 위에 그린 느낌, 종이 결이 살짝 비치는 분위기

[배경]
- 원본 사진의 배경은 사용하지 말 것
- 깨끗한 흰 종이 위에 그린 느낌으로 통일

[장식 요소]
- 인물 주변에 꽃, 사탕, 별, 하트, 구름, 무지개 같은 아기자기한 낙서 추가
- 동심 가득하고 사랑스러운 분위기

[인물 보존]
- 각 인물의 얼굴 특징, 헤어스타일, 안경, 구분 가능한 특징은 보존
- 단순화된 크레파스 스타일이지만 누가 누군지 알아볼 수 있어야 함

[형식]
- 정사각형(1:1) 비율, 모서리는 직각

[텍스트 규칙]
사용자가 명시적으로 문구를 요청하지 않으면 이미지에 글자를 그리지 말 것. 요청 시에만 귀여운 손글씨 폰트로 추가.`
    },
    { 
      id: 'free', 
      label: '자유', 
      systemPrompt: `[이미지 용도] 사용자가 원하는 자유로운 스타일의 이미지 생성
[스타일 가이드]
- 사용자의 프롬프트를 최대한 충실히 반영
- 고품질, 고해상도의 놀라운 이미지 생성
- 창의적이고 예술적인 표현
- 포토리얼리스틱, 일러스트, 3D, 시네마틱 등 다양한 스타일 지원`
    },
  ];
  
  // 용도별 예시 프롬프트 (각 카테고리에 맞는 맞춤 예시)
  const CATEGORY_EXAMPLE_PROMPTS = {
    explanation: [
      { label: '물의 순환', prompt: '물의 순환 과정을 보여주는 교육용 다이어그램. 바다에서 증발, 구름 형성, 비, 강으로 흐르는 과정을 화살표와 함께 단계별로 표시. 밝고 깨끗한 색상, 각 단계에 한글 라벨 포함.' },
      { label: '광합성', prompt: '식물의 광합성 과정을 보여주는 과학 다이어그램. 햇빛, 물, 이산화탄소가 들어가고 산소와 포도당이 나오는 과정. 잎 단면도와 엽록체를 포함한 교과서 스타일.' },
      { label: '분수 개념', prompt: '분수의 덧셈을 시각적으로 설명하는 그림. 동그란 피자를 1/2과 1/4로 나누어 보여주고, 합쳐서 3/4가 되는 과정을 단계별로 표현. 초등학생이 이해하기 쉬운 밝은 색상.' },
      { label: '태양계', prompt: '태양계의 행성들을 순서대로 보여주는 교육용 그림. 태양을 중심으로 수성, 금성, 지구, 화성, 목성, 토성, 천왕성, 해왕성이 궤도를 따라 배치. 각 행성 아래 한글 이름 표시.' },
    ],
    worksheet: [
      { label: '동물 색칠', prompt: '귀여운 강아지와 고양이가 함께 있는 색칠공부 도안. 굵은 외곽선으로 그려진 간단한 형태. 배경은 비워두고 동물만 그려진 상태. 유아나 초등 저학년 학생이 색칠하기 좋은 스타일.' },
      { label: '곤충 관찰', prompt: '나비, 무당벌레, 개미, 메뚜기 4가지 곤충을 보여주는 관찰 학습지용 그림. 각 곤충 옆에 이름을 쓸 수 있는 빈칸. 실제 곤충과 비슷하지만 귀엽게 단순화한 일러스트.' },
      { label: '시계 읽기', prompt: '시계 읽기 연습을 위한 학습지 그림. 커다란 아날로그 시계 4개가 각각 다른 시간을 가리키고 있음. 시침과 분침이 명확히 구분되는 깔끔한 시계 도안.' },
      { label: '식물 구조', prompt: '식물의 구조를 배우는 학습지용 그림. 꽃, 잎, 줄기, 뿌리가 있는 전체 식물 그림에 각 부분을 가리키는 화살표와 빈칸이 있어 학생이 이름을 직접 쓸 수 있음.' },
    ],
    ppt: [
      { label: '지구와 우주', prompt: '우주 공간에 떠 있는 아름다운 지구 이미지. 파란 바다와 흰 구름, 초록 대륙이 선명하게 보임. 검은 우주 배경에 별들이 반짝이는 고화질 스타일.' },
      { label: '인체 장기', prompt: '인체의 주요 장기들을 보여주는 의학 일러스트. 심장, 폐, 간, 위, 장이 제자리에 배치된 인체 실루엣. 각 장기는 다른 색상으로 구분. 깔끔한 배경.' },
      { label: '세계 지도', prompt: '세계 여러 나라를 다채로운 색상으로 구분한 평면 세계 지도. 각 대륙이 명확히 구분되고 주요 바다와 해양도 표시. PPT에 넣기 좋은 현대적인 플랫 스타일.' },
      { label: '계절 변화', prompt: '같은 나무가 봄, 여름, 가을, 겨울에 어떻게 변하는지 4장면으로 보여주는 그림. 각 계절의 특징이 명확히 드러나는 색감과 분위기. 가로로 배열된 구성.' },
    ],
    test: [
      { label: '도형 넓이', prompt: '정사각형, 직사각형, 삼각형, 원이 각각 그려진 시험 문제용 도형 그림. 각 도형에 변의 길이나 반지름이 숫자로 표시됨. 흑백 인쇄에 적합한 단순한 선 그림.' },
      { label: '그래프 해석', prompt: '막대그래프와 꺾은선그래프가 그려진 수학 시험 문제용 그림. x축과 y축에 눈금과 숫자가 명확히 표시됨. 깔끔하고 정확한 데이터 시각화 스타일.' },
      { label: '세포 구조', prompt: '동물 세포의 구조를 보여주는 과학 시험용 단면도. 핵, 세포막, 미토콘드리아, 골지체 등 주요 세포 소기관을 화살표로 가리킴. 레이블 없이 번호만 표시.' },
      { label: '한반도 지도', prompt: '한반도 윤곽 지도에 주요 산맥과 강이 표시된 지리 시험용 그림. 백두대간, 한강, 낙동강 등의 위치를 점선이나 실선으로 표현. 빈칸 채우기 문제에 적합한 단순한 도안.' },
    ],
    poster: [
      { label: '독서 권장', prompt: '책 읽는 것이 즐거워 보이는 교실 게시용 포스터. 다양한 어린이들이 즐겁게 책을 읽고 있는 모습. 상단에 "책과 함께하는 즐거운 시간" 텍스트. 밝고 활기찬 분위기.' },
      { label: '환경 보호', prompt: '지구를 소중히 지키자는 환경 교육 포스터. 아이들이 지구를 안고 있는 모습과 재활용, 나무 심기, 쓰레기 줍기 등의 작은 그림들. "우리가 지구를 지켜요" 메시지 포함.' },
      { label: '급식 예절', prompt: '학교 급식실에서 지켜야 할 예절을 알려주는 포스터. 줄 서기, 감사 인사, 음식 남기지 않기 등 3-4가지 규칙을 귀여운 캐릭터로 표현. 명랑하고 교육적인 분위기.' },
      { label: '안전 수칙', prompt: '학교 복도에서 지켜야 할 안전 수칙 포스터. 뛰지 않기, 오른쪽으로 걷기, 친구 밀지 않기 등을 픽토그램 스타일로 표현. 노란색과 검은색 안전 색상 사용.' },
    ],
    classPhoto: [
      { label: '졸업 축하', prompt: '위에 업로드한 학급 사진을 10살 아이가 그린 크레파스 그림처럼 변환해주세요. 흰 종이 위에 부드러운 파스텔 톤으로, 인물 주변에 졸업모, 꽃, 별 낙서를 아기자기하게 추가. 상단에 "졸업 축하해" 손글씨로 넣어주세요.' },
      { label: '우리 반 최고', prompt: '우리반 단체사진을 크레파스 손그림 스타일로 변환해주세요. 인물 주변에 사탕, 하트, 무지개 낙서를 추가하고 동심 가득한 분위기로. "우리 반 최고!" 손글씨를 가운데에 크게 넣어주세요.' },
      { label: '함께한 1년', prompt: '업로드한 사진을 따뜻한 크레파스 그림처럼 변환. 흰 종이 위에 그린 느낌, 부드러운 파스텔 톤. 작은 별과 구름 낙서. "함께한 1년, 고마워" 손글씨를 하단에 작게.' },
      { label: '사랑해요', prompt: '학급 사진을 동심 가득한 크레파스 그림으로 변환해주세요. 인물 주변에 분홍 하트와 별을 가득 그려넣고, "선생님 사랑해요" 손글씨를 함께 추가.' },
    ],
    free: [
      { label: '한옥 카페', prompt: '눈 내리는 겨울밤 따뜻한 불빛이 새어나오는 한옥 카페. 처마 끝에 고드름이 달려있고, 창문 너머로 은은한 조명과 커피잔이 보임. 마당에는 소복이 쌓인 눈과 돌담길. 포근하고 감성적인 분위기, 영화 같은 구도.' },
      { label: '삼겹살 클로즈업', prompt: '숯불 위에서 지글지글 익어가는 삼겹살 클로즈업. 기름이 튀고 연기가 피어오르는 순간. 옆에는 쌈채소, 마늘, 쌈장이 보임. 침이 고이는 먹방 스타일, 극도로 디테일한 음식 사진, 드라마틱한 조명.' },
      { label: '벚꽃 터널', prompt: '만개한 벚꽃 터널 아래 걸어가는 뒷모습. 꽃잎이 바람에 날리고, 햇살이 나뭇잎 사이로 스며들어 빛 줄기가 생김. 경주 또는 진해의 봄날 같은 분위기. 영화 포스터 같은 구도, 몽환적이고 아름다운 장면.' },
      { label: '미니어처 포차', prompt: '비 오는 밤 골목의 미니어처 포장마차. 노란 불빛이 따뜻하게 새어나오고, 비닐 천막에 빗방울이 맺혀있음. 안에는 작은 소주병과 안주가 보임. 미니어처 디오라마 스타일, 초정밀 디테일, 향수를 자극하는 분위기.' },
      { label: '귀여운 고양이', prompt: '창가에 앉아 비 오는 창밖을 바라보는 먼치킨 고양이. 빗소리가 들릴 것 같은 고요한 분위기. 옆에는 따뜻한 커피잔과 책. 부드러운 자연광, 아늑한 실내, 힐링되는 감성 사진.' },
      { label: '단풍 산사', prompt: '가을 단풍으로 물든 산속 작은 절. 빨강, 주황, 노랑 단풍이 절정이고, 이른 아침 안개가 살짝 끼어있음. 절 마당에는 스님이 빗자루로 낙엽을 쓸고 있음. 한국의 가을 감성, 평화롭고 고즈넉한 풍경.' },
    ],
  };
  
  // 레퍼런스 이미지 상태
  const [referenceImage, setReferenceImage] = useState(null);
  const [referenceImagePreview, setReferenceImagePreview] = useState(null);
  
  // 레퍼런스 이미지 업로드 핸들러
  const handleReferenceImageUpload = (e) => {
    // 권한 확인
    if (!checkAccess()) {
      e.target.value = ''; // 파일 선택 초기화
      return;
    }
    
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        showToast('이미지는 10MB 이하만 올릴 수 있어요', 'warning');
        return;
      }
      setReferenceImage(file);
      // 미리보기 생성
      const reader = new FileReader();
      reader.onloadend = () => {
        setReferenceImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // 레퍼런스 이미지 제거
  const handleRemoveReferenceImage = () => {
    setReferenceImage(null);
    setReferenceImagePreview(null);
  };
  
  // 현재 카테고리의 예시 프롬프트 가져오기
  const currentExamples = CATEGORY_EXAMPLE_PROMPTS[imageCategory] || CATEGORY_EXAMPLE_PROMPTS.explanation;
  
  // 이미지 생성 에러 메시지 상태
  const [imageError, setImageError] = useState('');
  
  // 이미지 생성 제출
  const handleImageGenerate = async () => {
    if (!checkAccess()) return;
    
    if (!imagePrompt.trim()) return;
    
    setIsGeneratingImage(true);
    setGeneratedImage(null);
    setImageError('');
    
    try {
      const token = currentUser ? await currentUser.getIdToken() : null;
      if (!token) {
        setImageError('로그인이 필요해요. 다시 로그인해주세요.');
        return;
      }
      
      const categoryInfo = IMAGE_CATEGORIES.find(c => c.id === imageCategory);
      const systemPrompt = categoryInfo?.systemPrompt || '';
      
      let referenceImageBase64 = null;
      if (referenceImage) {
        referenceImageBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(referenceImage);
        });
      }
      
      const response = await fetch(getApiUrl('/api/lab/generate-image'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          prompt: imagePrompt,
          category: imageCategory,
          systemPrompt: systemPrompt,
          referenceImage: referenceImageBase64
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        const detail = errorData?.detail;
        throw new Error(
          (detail && typeof detail === 'object' ? detail.message : detail) || '이미지를 만들 수 없었어요.'
        );
      }
      
      const data = await response.json();
      
      if (data.success && data.image_url) {
        setGeneratedImage(data.image_url);
      } else {
        throw new Error(data.message || '이미지를 만들 수 없었어요.');
      }
    } catch (error) {
      console.error('이미지 생성 오류:', error);
      const msg = error.message || '';
      if (msg.includes('403') || msg.includes('PRO')) {
        setImageError('PRO 구독이 필요한 기능이에요.');
      } else if (msg.includes('바빠서') || msg.includes('밀리고') || msg.includes('저작권') || msg.includes('구체적') || msg.includes('만들지 못했어요') || msg.includes('만들 수 없어요')) {
        setImageError(msg);
      } else if (msg.includes('다시 시도')) {
        setImageError(msg);
      } else {
        setImageError('이미지를 만들지 못했어요. 잠시 후 다시 시도해주세요.');
      }
    } finally {
      setIsGeneratingImage(false);
    }
  };
  
  // 이미지 다운로드
  const handleImageDownload = () => {
    if (!generatedImage) return;
    
    const link = document.createElement('a');
    link.href = generatedImage;
    link.download = `whatquiz-image-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast('다운로드 폴더에 저장되었어요', 'success', 20000, {
      label: '다시 열기',
      onClick: () => window.open(generatedImage, '_blank'),
    });
  };

  // 🔍 평가 기준 파일 사전 검사 수행
  const performRubricFilePreCheck = useCallback(async (file) => {
    // 로딩 시작
    setPreCheckLoading(true);
    setShowPreCheckModal(true);
    
    try {
      const result = await preCheckFiles([file], 'lab', isPro);
      
      // 항상 검사 결과를 모달로 표시
      setPreCheckResult(result);
      setPendingRubricFile(file);
      setPreCheckLoading(false);
      
    } catch (error) {
      console.error('파일 사전 검사 오류:', error);
      // 오류 시에도 결과를 모달로 표시
      setPreCheckResult({
        can_proceed: true,
        needs_confirmation: true,
        overall_status: 'warning',
        total_files: 1,
        files_info: [{ filename: file.name, status: 'ok' }],
        recommendations: ['파일 검사 중 오류가 발생했습니다. 계속 진행할 수 있습니다.']
      });
      setPendingRubricFile(file);
      setPreCheckLoading(false);
    }
  }, [isPro]);  // isPro 는 사전검사 한도 표기용

  // 평가 기준 파일 업로드 핸들러 (사전 검사 호출)
  const handleRubricFileUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    performRubricFilePreCheck(file);
  }, [performRubricFilePreCheck]);

  // 실제 평가 기준 파일 업로드
  const actualRubricFileUpload = useCallback(async (file) => {
    setRubricFile(file);
    setRubricFileUploading(true);

    try {
      // FormData 생성
      const formData = new FormData();
      formData.append('file', file);

      // 토큰 가져오기
      const token = currentUser ? await currentUser.getIdToken() : null;
      if (!token) {
        setShowLoginModal(true);
        setRubricFileUploading(false);
        return;
      }

      // API 호출하여 파일 내용 추출
      const response = await fetch(getApiUrl('/api/lab/extract-rubric'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '파일 분석에 실패했습니다.');
      }

      const data = await response.json();
      
      if (data.success && data.extracted_text) {
        // 추출된 텍스트를 평가 기준 입력란에 자동 입력
        setRubricText(data.extracted_text);
        showToast('평가 기준을 자동으로 채웠어요', 'success');
      } else {
        throw new Error(data.message || '파일에서 내용을 추출할 수 없습니다.');
      }
    } catch (error) {
      console.error('평가 기준 파일 분석 실패:', error);
      showToast('파일을 분석하지 못했어요. 다시 시도해주세요', 'error');
      setRubricFile(null);
    } finally {
      setRubricFileUploading(false);
    }
  }, [currentUser, showToast]);
  
  // 🔧 ref 업데이트 (최신 함수 참조 유지)
  actualRubricFileUploadRef.current = actualRubricFileUpload;

  // 평가 기준 파일 제거
  const handleRubricFileRemove = useCallback(() => {
    setRubricFile(null);
  }, []);

  // 🔍 파일 사전 검사 수행 (답안 파일) - ChatPage.js와 동일한 합산 제한 체크
  const performAnswerFilesPreCheck = useCallback(async (files) => {
    // 로딩 시작
    setPreCheckLoading(true);
    setShowPreCheckModal(true);
    
    // 📎 제한 상수 (Single Source of Truth: constants/fileLimits.js)
    // Free/Pro 공통: 200,000자
    const MAX_FILES = MAX_FILES_DEFAULT;
    const { contentLength: MAX_CONTENT_LENGTH } = getFileLimits(isPro);
    
    // 🆕 이미 첨부된 파일 정보 합계
    const currentStats = {
      fileCount: answerFiles.length,
      ...summarizeFileStats(answerFilesStats),
    };
    
    // 🆕 파일 수 초과 체크 (프론트에서 먼저 체크)
    const totalFileCount = currentStats.fileCount + files.length;
    if (totalFileCount > MAX_FILES) {
      const availableSlots = Math.max(0, MAX_FILES - currentStats.fileCount);
      setPreCheckResult({
        can_proceed: false,
        needs_confirmation: false,
        overall_status: 'error',
        total_files: files.length,
        total_size: files.reduce((sum, f) => sum + f.size, 0),
        total_size_display: `${(files.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024).toFixed(1)}MB`,
        files_info: files.map(f => ({
          filename: f.name,
          status: 'ok',
          file_size: f.size,
          file_size_display: `${(f.size / 1024).toFixed(1)}KB`,
          issues: []
        })),
        recommendations: availableSlots > 0 
          ? [`현재 ${availableSlots}개의 파일만 추가로 업로드 가능합니다.`]
          : ['기존 파일을 삭제한 후 다시 시도해주세요.'],
        file_count_error: `파일은 최대 ${MAX_FILES}개까지 업로드 가능합니다. (현재 ${currentStats.fileCount}개 첨부됨, ${files.length}개 추가 시도)`
      });
      setPendingAnswerFiles(null);
      setPreCheckLoading(false);
      return;
    }

    const combinedFiles = [...answerFiles, ...files];
    const totalRequestFileSize = combinedFiles.reduce((sum, f) => sum + (f.size || 0), 0);
    const MAX_REQUEST_FILE_SIZE = 25 * 1024 * 1024;
    if (totalRequestFileSize > MAX_REQUEST_FILE_SIZE) {
      setPreCheckResult({
        can_proceed: false,
        needs_confirmation: false,
        overall_status: 'error',
        title: '파일 용량을 줄여주세요',
        message: `답안 파일 합계가 ${(totalRequestFileSize / 1024 / 1024).toFixed(1)}MB예요. 한 번에 25MB까지만 첨부할 수 있어요.`,
        total_files: files.length,
        total_size: totalRequestFileSize,
        total_size_display: `${(totalRequestFileSize / 1024 / 1024).toFixed(1)}MB`,
        files_info: files.map(f => ({
          filename: f.name,
          status: 'warning',
          file_size: f.size,
          file_size_display: `${(f.size / 1024 / 1024).toFixed(1)}MB`,
          issues: [],
        })),
        recommendations: ['파일을 압축하거나 나눠서 첨부해 주세요.'],
      });
      setPendingAnswerFiles(null);
      setPreCheckLoading(false);
      return;
    }
    
    try {
      const result = await preCheckFiles(files, 'lab', isPro);
      
      // 🆕 새 파일들의 페이지/슬라이드/문자수 합계 (백엔드에서 실제 추출한 값 사용)
      const newFileStats = normalizePreCheckStats(result.files_info || []);
      const newStats = summarizeFileStats(newFileStats);
      
      // 🆕 합산 제한 체크
      const totalPages = currentStats.pageCount + newStats.pageCount;
      const totalSlides = currentStats.slideCount + newStats.slideCount;
      const totalContentLength = currentStats.contentLength + newStats.contentLength;
      
      const exceededLimits = [];
      
      if (totalPages > MAX_PDF_PAGES) {
        exceededLimits.push(`PDF 페이지: 현재 ${currentStats.pageCount}페이지 + 새 파일 ${newStats.pageCount}페이지 = 총 ${totalPages}페이지 (최대 ${MAX_PDF_PAGES}페이지)`);
      }
      
      if (totalSlides > MAX_PPT_SLIDES) {
        exceededLimits.push(`PPT 슬라이드: 현재 ${currentStats.slideCount}개 + 새 파일 ${newStats.slideCount}개 = 총 ${totalSlides}개 (최대 ${MAX_PPT_SLIDES}개)`);
      }
      
      if (totalContentLength > MAX_CONTENT_LENGTH) {
        exceededLimits.push(`총 분석량: 현재 ${currentStats.contentLength.toLocaleString()}자 + 새 파일 ${newStats.contentLength.toLocaleString()}자 = 총 ${totalContentLength.toLocaleString()}자 (최대 ${MAX_CONTENT_LENGTH.toLocaleString()}자)`);
      }
      
      // 🆕 합산 초과 시 경고 추가
      if (exceededLimits.length > 0) {
        const modifiedResult = {
          ...result,
          needs_confirmation: true,
          overall_status: result.overall_status === 'ok' ? 'warning' : result.overall_status,
          recommendations: [
            ...(result.recommendations || []),
            ...exceededLimits.map(limit => `⚠️ ${limit}`)
          ],
          combined_warning: `이미 첨부된 파일과 합쳐서 제한을 초과합니다. 초과분은 분석에서 제외됩니다.`
        };
        
        // 🆕 결과에 새 파일 통계 저장 (업로드 시 사용)
        modifiedResult._newFilesStats = newFileStats;
        
        setPreCheckResult(modifiedResult);
        setPendingAnswerFiles(files);
        setPreCheckLoading(false);
        return;
      }
      
      // 🆕 결과에 새 파일 통계 저장 (업로드 시 사용)
      result._newFilesStats = newFileStats;
      
      // 항상 검사 결과를 모달로 표시
      setPreCheckResult(result);
      setPendingAnswerFiles(files);
      setPreCheckLoading(false);
      
    } catch (error) {
      console.error('파일 사전 검사 오류:', error);
      // 오류 시에도 결과를 모달로 표시
      setPreCheckResult({
        can_proceed: true,
        needs_confirmation: true,
        overall_status: 'warning',
        total_files: files.length,
        files_info: files.map(f => ({ filename: f.name, status: 'ok' })),
        recommendations: ['파일 검사 중 오류가 발생했습니다. 계속 진행할 수 있습니다.'],
        _newFilesStats: []
      });
      setPendingAnswerFiles(files);
      setPreCheckLoading(false);
    }
  }, [answerFiles, answerFilesStats, isPro]);  // 🔧 합산 체크 + Pro 한도 동기화

  // 🔍 사전 검사 확인 후 업로드 진행
  const handlePreCheckConfirm = useCallback(() => {
    setShowPreCheckModal(false);
    if (pendingAnswerFiles) {
      // 🆕 파일 통계도 함께 전달
      const filesStats = preCheckResult?._newFilesStats || [];
      actualAnswerFilesUploadRef.current(pendingAnswerFiles, filesStats);
      setPendingAnswerFiles(null);
    }
    if (pendingRubricFile) {
      actualRubricFileUploadRef.current(pendingRubricFile);  // 🔧 ref를 통해 최신 함수 호출
      setPendingRubricFile(null);
    }
    setPreCheckResult(null);
  }, [pendingAnswerFiles, pendingRubricFile, preCheckResult]);  // 🆕 preCheckResult 추가 (통계 전달용)

  // 🔍 사전 검사 취소
  const handlePreCheckCancel = useCallback(() => {
    setShowPreCheckModal(false);
    setPendingAnswerFiles(null);
    setPendingRubricFile(null);
    setPreCheckResult(null);
  }, []);

  // 📎 실제 답안 파일 업로드
  const actualAnswerFilesUpload = useCallback((files, filesStats = []) => {
    setAnswerFiles(prev => [...prev, ...files]);
    setAnswerFilesStats(prev => [...prev, ...filesStats]);  // 🆕 통계도 저장
  }, []);
  
  // 🔧 ref 업데이트 (최신 함수 참조 유지)
  actualAnswerFilesUploadRef.current = actualAnswerFilesUpload;

  // 답안 파일 업로드 (사전 검사 포함)
  const handleAnswerFilesUpload = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    const MAX_FILES = MAX_FILES_DEFAULT;
    // 📎 파일 크기 (Pro 30MB / Free 30MB)
    const { fileSize: MAX_SIZE } = getFileLimits(isPro);
    const maxSizeMB = Math.round(MAX_SIZE / (1024 * 1024));
    
    // 파일 수 제한 체크
    const totalFiles = answerFiles.length + files.length;
    if (totalFiles > MAX_FILES) {
      showToast(`파일은 최대 ${MAX_FILES}개까지 올릴 수 있어요`, 'warning');
      return;
    }
    
    // 파일 크기 체크
    const oversizedFiles = files.filter(f => f.size > MAX_SIZE);
    if (oversizedFiles.length > 0) {
      showToast(`파일 하나는 ${maxSizeMB}MB 이하로 올려주세요`, 'warning');
      return;
    }
    
    // 파일 사전 검사 수행
    performAnswerFilesPreCheck(files);
  }, [answerFiles.length, performAnswerFilesPreCheck, isPro, showToast]);

  // 답안 파일 제거
  const handleAnswerFileRemove = useCallback((index) => {
    setAnswerFiles(prev => prev.filter((_, i) => i !== index));
    setAnswerFilesStats(prev => prev.filter((_, i) => i !== index));  // 🆕 통계도 제거
  }, []);

  // 드래그 앤 드롭 핸들러
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    // 권한 확인 (비로그인 -> 로그인 모달, FREE -> PRO 모달)
    if (!checkAccess()) {
      return;
    }
    
    const files = Array.from(e.dataTransfer.files);
    const MAX_FILES = MAX_FILES_DEFAULT;
    // 📎 파일 크기 (Pro 30MB / Free 30MB)
    const { fileSize: MAX_SIZE } = getFileLimits(isPro);
    const maxSizeMB = Math.round(MAX_SIZE / (1024 * 1024));
    
    // 파일 수 제한 체크
    const totalFiles = answerFiles.length + files.length;
    if (totalFiles > MAX_FILES) {
      showToast(`파일은 최대 ${MAX_FILES}개까지 올릴 수 있어요`, 'warning');
      return;
    }
    
    // 파일 크기 체크
    const oversizedFiles = files.filter(f => f.size > MAX_SIZE);
    if (oversizedFiles.length > 0) {
      showToast(`파일 하나는 ${maxSizeMB}MB 이하로 올려주세요`, 'warning');
      return;
    }
    
    // 파일 사전 검사 수행
    performAnswerFilesPreCheck(files);
  }, [checkAccess, answerFiles.length, performAnswerFilesPreCheck, isPro, showToast]);

  // 엑셀 다운로드
  const handleDownloadExcel = useCallback(() => {
    if (evaluationResults.length === 0) return;

    try {
      // xlsx 라이브러리 동적 import
      import('xlsx').then((XLSX) => {
        // 엑셀 데이터 준비
        const excelData = evaluationResults.map((result, index) => {
          const row = {
            '번호': index + 1,
            '학생명': result.studentName,
            '파일명': result.fileName,
            '총점': result.score || 0,
            '총평': result.feedback || '',
          };

          // 항목별 점수 추가
          if (result.item_scores && result.item_scores.length > 0) {
            result.item_scores.forEach((item) => {
              row[`${item.item_name} (${item.max_score}점)`] = item.score;
            });
          }

          // 잘한 점
          if (result.strengths && result.strengths.length > 0) {
            row['잘한 점'] = result.strengths.join(', ');
          }

          // 개선점
          if (result.improvements && result.improvements.length > 0) {
            row['개선점'] = result.improvements.join(', ');
          }

          // 에러가 있는 경우
          if (result.error) {
            row['오류'] = result.error;
          }

          return row;
        });

        // 워크시트 생성
        const worksheet = XLSX.utils.json_to_sheet(excelData);
        
        // 컬럼 너비 설정
        const columnWidths = [
          { wch: 5 },   // 번호
          { wch: 10 },  // 학생명
          { wch: 30 },  // 파일명
          { wch: 8 },   // 총점
          { wch: 50 },  // 총평
        ];
        worksheet['!cols'] = columnWidths;

        // 워크북 생성
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, '평가 결과');

        // 파일명 생성 (날짜 포함)
        const today = new Date();
        const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
        const filename = `서술형채점결과_${dateStr}.xlsx`;

        // 파일 다운로드
        XLSX.writeFile(workbook, filename);
        showToast('다운로드 폴더에 저장되었어요', 'success', 10000);
      });
    } catch (error) {
      console.error('엑셀 다운로드 실패:', error);
      showToast('엑셀 다운로드에 실패했어요', 'error');
    }
  }, [evaluationResults, showToast]);

  // 답안 평가 제출
  const handleEvaluationSubmit = async () => {
    // 권한 확인 (비로그인 -> 로그인 모달, FREE -> PRO 모달)
    if (!checkAccess()) {
      return;
    }
    
    if (answerFiles.length === 0) {
      showToast('학생 답안 파일을 1개 이상 올려주세요', 'warning');
      return;
    }

    setIsLoading(true);
    setEvaluationResults([]);

    try {
      // FormData 생성
      const formData = new FormData();
      
      // 평가 기준 텍스트만 추가 (파일에서 추출된 내용이 이미 텍스트에 있음)
      if (rubricText) {
        formData.append('rubric_text', rubricText);
      }
      
      // 답안 파일들 추가
      answerFiles.forEach((file) => {
        formData.append('answer_files', file);
      });

      // 토큰 가져오기
      const token = currentUser ? await currentUser.getIdToken() : null;
      if (!token) {
        setShowLoginModal(true);
        return;
      }

      // API 호출
      const response = await fetch(getApiUrl('/api/lab/evaluate-answers'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '평가 중 오류가 발생했습니다.');
      }

      const data = await response.json();
      
      if (data.success) {
        setEvaluationResults(data.results);
      } else {
        throw new Error(data.message || '평가 실패');
      }
    } catch (error) {
      console.error('평가 오류:', error);
      showToast('채점하지 못했어요. 잠시 후 다시 시도해주세요', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // 카테고리 선택 화면
  if (!selectedFeature) {
    return (
      <>
        <Helmet>
          <title>실험실 - 왓퀴즈</title>
          <meta name="description" content="왓퀴즈 실험실 - 새로운 AI 기능을 먼저 체험해보세요" />
        </Helmet>

        <div className="labmain-page">
          <div className="labmain-header">
            <span className="labmain-pro-tag">PRO</span>
            <h1 className="labmain-title">실험실</h1>
            <p className="labmain-desc">
              새로운 기능을 먼저 써보고 의견을 들려주세요
            </p>
          </div>
          
          <div className="labmain-grid">
            {Object.values(FEATURES).map((feature) => (
              <button 
                key={feature.id}
                className={`labmain-card ${!feature.available ? 'labmain-card--disabled' : ''}`}
                onClick={() => handleFeatureSelect(feature.id)}
              >
                <div className="labmain-card-top">
                  <div className="labmain-card-icon">
                    {feature.id === 'imageGeneration' && (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.8"/><circle cx="8.5" cy="8.5" r="2" stroke="currentColor" strokeWidth="1.5"/><path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    )}
                    {feature.id === 'pptGeneration' && (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.8"/><path d="M8 21h8M12 17v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                    )}
                  </div>
                  {feature.isNew && feature.available && (
                    <span className="labmain-new">NEW</span>
                  )}
                  {!feature.available && (
                    <span className="labmain-soon">준비중</span>
                  )}
                </div>
                <h3 className="labmain-card-title">{feature.title}</h3>
                <p className="labmain-card-desc">{feature.subtitle}</p>
              </button>
            ))}
          </div>
        </div>

        {/* 준비 중 기능 안내 모달 — 껍데기는 common/Dialog, 버튼은 ModalActions */}
        <Dialog
          description={comingSoonFeature?.id === 'imageGeneration'
            ? 'AI가 수업에 필요한 일러스트, 다이어그램, 포스터를 만들어드려요. 곧 만나볼 수 있어요.'
            : '주제만 입력하면 AI가 슬라이드 구성부터 내용까지 만들어드려요. 조금만 기다려주세요.'}
          eyebrow="준비 중"
          footer={(
            <ModalActions
              primary={{ label: '알겠어요', onClick: () => setShowComingSoonModal(false) }}
              testId="lab-coming-soon-actions"
            />
          )}
          onClose={() => setShowComingSoonModal(false)}
          open={Boolean(showComingSoonModal && comingSoonFeature)}
          size="sm"
          testId="lab-coming-soon"
          title={comingSoonFeature?.title}
        >
          <ul className="lab-modal-features">
            {comingSoonFeature?.id === 'imageGeneration' ? (
              <>
                <li>수업용 일러스트</li>
                <li>다이어그램과 인포그래픽</li>
                <li>게시물과 포스터 이미지</li>
              </>
            ) : (
              <>
                <li>주제 기반 슬라이드 구성</li>
                <li>내용 자동 생성과 정리</li>
                <li>깔끔한 디자인 템플릿</li>
              </>
            )}
          </ul>
        </Dialog>
        
        {/* 로그인 모달 */}
        <LoginPrompt
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          feature="lab"
        />
        
        {/* PRO 업그레이드 모달 */}
        <ProUpgradeModal
          isOpen={showProRequiredModal}
          onClose={() => setShowProRequiredModal(false)}
          feature="실험실"
        />
        
        {/* 🔍 파일 사전 검사 모달 */}
        <FilePreCheckModal
          isOpen={showPreCheckModal}
          onClose={handlePreCheckCancel}
          onConfirm={handlePreCheckConfirm}
          checkResult={preCheckResult}
          isLoading={preCheckLoading}
        />
      </>
    );
  }

  // 서술형 채점 기능 화면
  if (selectedFeature === 'evaluation') {
    return (
      <>
        <Helmet>
          <title>서술형 채점 - 실험실 - 왓퀴즈</title>
        </Helmet>

        <div className="lab-feature-page">
          <div className="lab-feature-header">
            <button onClick={handleBackToSelection} className="lab-back-button">
              ← 뒤로가기
            </button>
            <div>
              <h1 className="lab-feature-title">
                서술형 채점
                <span className="lab-beta-badge-small">BETA</span>
              </h1>
              <p className="lab-feature-subtitle">
                AI가 서술형 답안을 채점 기준에 따라 정확하게 채점합니다
              </p>
            </div>
          </div>

          <div className="lab-feature-content">
            {/* Step 1: 수행평가 계획서/채점 기준서 (선택사항) */}
            <div className="lab-section">
              <h2 className="lab-section-title">1. 서술형 채점 기준서 (선택)</h2>
              <p className="lab-section-subtitle">1개의 수행평가에 대한 세부 채점 기준표를 업로드하면 AI가 자동으로 분석합니다</p>
              
              <div className="lab-rubric-container">
                <textarea
                  value={rubricText}
                  onChange={(e) => setRubricText(e.target.value)}
                  placeholder="채점 기준표를 붙여넣으세요"
                  className="lab-textarea-simple"
                  rows={8}
                  disabled={rubricFileUploading}
                />
                
                <div className="lab-file-or">또는</div>
                
                <div className="lab-file-upload-zone">
                  {rubricFileUploading ? (
                    // 파일 분석 중
                    <div className="lab-file-analyzing">
                      <div className="lab-analyzing-spinner"></div>
                      <div className="lab-analyzing-text">
                        <strong>AI가 파일을 분석하고 있습니다...</strong>
                        <p>평가 기준을 추출하고 있어요. 잠시만 기다려주세요.</p>
                      </div>
                    </div>
                  ) : rubricFile ? (
                    // 파일 업로드 완료
                    <div className="lab-uploaded-file">
                      <span className="lab-file-name">{rubricFile.name}</span>
                      <button 
                        onClick={handleRubricFileRemove}
                        className="lab-file-remove-btn"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    // 파일 업로드 대기
                    <label 
                      className="lab-file-upload-label"
                      onClick={(e) => {
                        if (!checkAccess()) {
                          e.preventDefault();
                        }
                      }}
                    >
                      <input
                        type="file"
                        accept=".pdf,.ppt,.pptx,.jpg,.jpeg,.png,.hwp,.hwpx,.docx,.xlsx,.xls"
                        onChange={handleRubricFileUpload}
                        className="lab-file-input-hidden"
                      />
                      <div className="lab-file-upload-text">
                        수행평가 계획서 / 채점 기준서 파일 첨부
                        <span className="lab-file-format">(PDF, 이미지, HWP, PPT, Excel)</span>
                      </div>
                    </label>
                  )}
                </div>
              </div>
            </div>

            {/* Step 2: 학생 과제 업로드 */}
            <div className="lab-section">
              <h2 className="lab-section-title">2. 학생 과제 업로드</h2>
              <p className="lab-section-subtitle">채점할 학생 과제 파일을 업로드하세요 (여러 파일 동시 업로드 가능)</p>
              
              <div 
                className={`lab-dropzone ${isDragging ? 'dragging' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  multiple
                  accept=".pdf,.ppt,.pptx,.jpg,.jpeg,.png,.heic,.heif,.hwp,.hwpx,.docx"
                  onChange={handleAnswerFilesUpload}
                  className="lab-file-input-hidden"
                  id="answer-files-input"
                />
                <label 
                  htmlFor="answer-files-input" 
                  className="lab-dropzone-label"
                  onClick={(e) => {
                    if (!checkAccess()) {
                      e.preventDefault();
                    }
                  }}
                >
                  <div className="lab-dropzone-icon"></div>
                  <p className="lab-dropzone-text">
                    학생 과제 파일을 드래그하거나 클릭하여 업로드
                  </p>
                  <p className="lab-dropzone-formats">
                    이미지, PDF, HWP, PPT 지원 / 최대 {MAX_FILES_DEFAULT}개 파일
                  </p>
                </label>
              </div>

              {/* 업로드된 파일 목록 */}
              {answerFiles.length > 0 && (
                <div className="lab-files-list">
                  <div className="lab-files-list-header">
                    업로드된 파일 ({answerFiles.length}개)
                  </div>
                  {answerFiles.map((file, index) => (
                    <div key={index} className="lab-file-item">
                      <span className="lab-file-item-name">{file.name}</span>
                      <span className="lab-file-item-size">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                      <button
                        onClick={() => handleAnswerFileRemove(index)}
                        className="lab-file-item-remove"
                      >
                        삭제
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 평가 시작 버튼 */}
            <button
              onClick={handleEvaluationSubmit}
              disabled={isLoading || answerFiles.length === 0 || rubricFileUploading || preCheckLoading}
              className="lab-submit-button-large"
            >
              {isLoading ? '채점 중...' : rubricFileUploading ? '기준서 분석 중...' : preCheckLoading ? '파일 검사 중...' : `채점 시작 (${answerFiles.length}개 파일)`}
            </button>

            {/* 로딩 상태 */}
            {isLoading && (
              <div className="lab-loading">
                <div className="lab-loading-spinner"></div>
                <p>AI가 답안을 분석하고 있습니다...</p>
              </div>
            )}

            {/* 평가 결과 */}
            {evaluationResults.length > 0 && !isLoading && (
              <div className="lab-results-section">
                <h2 className="lab-results-title">
                  평가 결과 ({evaluationResults.length}명)
                </h2>
                
                {evaluationResults.map((result, index) => (
                  <div key={index} className="lab-result-student-card">
                    {result.error ? (
                      // 에러가 있는 경우
                      <div className="lab-result-error">
                        <h3 className="lab-result-student-name">{result.studentName}</h3>
                        <div className="lab-result-student-file">
                          파일: {result.fileName}
                        </div>
                        <p className="lab-error-message">{result.error}</p>
                      </div>
                    ) : (
                      // 정상 평가 결과
                      <>
                        <div className="lab-result-student-header">
                          <h3 className="lab-result-student-name">
                            {result.studentName}
                          </h3>
                          <div className="lab-result-student-score">
                            {result.total_score || result.score}점
                            {result.base_points > 0 && (
                              <span className="lab-result-base-points">
                                (기본 {result.base_points}점 포함)
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="lab-result-student-file">
                          파일: {result.fileName}
                        </div>

                        <div className="lab-result-section">
                          <h4 className="lab-result-section-title">총평</h4>
                          <p className="lab-result-text">{result.feedback}</p>
                        </div>

                        {/* 항목별 점수 표시 */}
                        {result.item_scores && result.item_scores.length > 0 && (
                          <div className="lab-result-section">
                            <h4 className="lab-result-section-title">항목별 채점</h4>
                            <div className="lab-item-scores">
                              {result.item_scores.map((item, idx) => (
                                <div key={idx} className="lab-item-score">
                                  <div className="lab-item-score-header">
                                    <span className="lab-item-name">{item.item_name}</span>
                                    <span className="lab-item-points">
                                      {item.score} / {item.max_score}점
                                    </span>
                                  </div>
                                  {item.matched_level && (
                                    <p className="lab-item-level">
                                      수행수준: {item.matched_level}
                                    </p>
                                  )}
                                  <p className="lab-item-reason">{item.reason}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="lab-result-columns">
                          <div className="lab-result-column">
                            <h4 className="lab-result-section-title">잘한 점</h4>
                            <ul className="lab-result-list-simple">
                              {result.strengths && result.strengths.map((item, i) => (
                                <li key={i}>{item}</li>
                              ))}
                            </ul>
                          </div>

                          <div className="lab-result-column">
                            <h4 className="lab-result-section-title">개선점</h4>
                            <ul className="lab-result-list-simple">
                              {result.improvements && result.improvements.map((item, i) => (
                                <li key={i}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        </div>

                        {/* 교사의 총평 (생기부 스타일) */}
                        {(result.teacher_comment || result.feedback) && (
                          <div className="lab-result-section">
                            <h4 className="lab-result-section-title">교사의 총평</h4>
                            <div className="lab-teacher-comment">
                              <p>{result.teacher_comment || result.feedback}</p>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}

                <button 
                  className="lab-download-button"
                  onClick={handleDownloadExcel}
                >
                  결과 엑셀로 다운로드
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* 로그인 모달 */}
        <LoginPrompt
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          feature="lab"
        />
        
        {/* PRO 업그레이드 모달 */}
        <ProUpgradeModal
          isOpen={showProRequiredModal}
          onClose={() => setShowProRequiredModal(false)}
          feature="서술형 채점"
        />
      </>
    );
  }

  // 이미지 생성 기능 화면
  if (selectedFeature === 'imageGeneration') {
    return (
      <>
        <Helmet>
          <title>AI 이미지 생성 - 왓퀴즈</title>
          <meta name="description" content="AI로 수업용 이미지를 만들어보세요. 개념 설명, 학습지, PPT용 이미지를 프롬프트 한 줄로 생성합니다." />
        </Helmet>

        <div className="labimg-page">
          {/* 헤더 */}
          <div className="labimg-header">
            <button onClick={handleBackToSelection} className="labimg-back">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
              실험실
            </button>
            <div className="labimg-header-text">
              <h1 className="labimg-title">이미지 생성</h1>
              <span className="labimg-badge">BETA</span>
            </div>
            <p className="labimg-desc">원하는 이미지를 설명하면 AI가 만들어드려요</p>
          </div>

          {/* 메인 컨텐츠 */}
          <div className="labimg-content">
            {/* 용도 선택 */}
            <div className="labimg-section">
              <label className="labimg-label">어디에 사용하시나요?</label>
              <div className="labimg-category-row">
                {IMAGE_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    className={`labimg-category-chip ${imageCategory === cat.id ? 'labimg-category-chip--active' : ''}`}
                    onClick={() => setImageCategory(cat.id)}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 참고 이미지 */}
            <div className="labimg-section">
              <label className="labimg-label">
                참고 이미지
                <span className="labimg-label-optional">선택</span>
              </label>
              {!referenceImagePreview ? (
                <label 
                  className="labimg-ref-upload"
                  onClick={(e) => {
                    if (!checkAccess()) {
                      e.preventDefault();
                    }
                  }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleReferenceImageUpload}
                    className="lab-file-input-hidden"
                    disabled={isGeneratingImage}
                  />
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="labimg-ref-icon"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  <span className="labimg-ref-text">비슷한 스타일의 이미지가 있다면 올려주세요</span>
                  <span className="labimg-ref-hint">10MB 이하 이미지</span>
                </label>
              ) : (
                <div className="labimg-ref-preview">
                  <img src={referenceImagePreview} alt="참고 이미지" className="labimg-ref-img" />
                  <button 
                    onClick={handleRemoveReferenceImage} 
                    className="labimg-ref-remove"
                    disabled={isGeneratingImage}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  </button>
                </div>
              )}
            </div>

            {/* 이미지 설명 */}
            <div className="labimg-section">
              <label className="labimg-label">어떤 이미지가 필요하세요?</label>
              
              {/* 예시 칩 */}
              <div className="labimg-examples">
                {currentExamples.map((example, idx) => (
                  <button
                    key={idx}
                    className="labimg-example-chip"
                    onClick={() => setImagePrompt(example.prompt)}
                    disabled={isGeneratingImage}
                  >
                    {example.label}
                  </button>
                ))}
              </div>
              
              <textarea
                value={imagePrompt}
                onChange={(e) => setImagePrompt(e.target.value)}
                placeholder="예시를 클릭하거나, 원하는 이미지를 자유롭게 설명해주세요"
                className="labimg-textarea"
                rows={4}
                disabled={isGeneratingImage}
              />
              
              <div className="labimg-tips">
                <span className="labimg-tips-icon">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.2"/><path d="M7 6.5V10M7 4.5V4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
                </span>
                구체적으로 설명할수록 좋은 결과를 얻을 수 있어요
              </div>
            </div>

            {/* 생성 버튼 */}
            <button
              onClick={handleImageGenerate}
              disabled={isGeneratingImage || !imagePrompt.trim()}
              className="labimg-generate-btn"
            >
              {isGeneratingImage ? (
                <span className="labimg-btn-loading">
                  <span className="labimg-btn-spinner"></span>
                  만들고 있어요...
                </span>
              ) : '만들기'}
            </button>
          </div>

          {/* 로딩 상태 */}
          {isGeneratingImage && (
            <div className="labimg-loading-card">
              <div className="labimg-loading-bar">
                <div className="labimg-loading-progress"></div>
              </div>
              <p className="labimg-loading-text">AI가 이미지를 그리고 있어요</p>
              <p className="labimg-loading-sub">보통 30초~1분 정도 걸려요</p>
            </div>
          )}

          {/* 에러 메시지 */}
          {imageError && !isGeneratingImage && (
            <div className="labimg-error">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="8" stroke="#EF4444" strokeWidth="1.5"/><path d="M10 6.5v4M10 13v.5" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round"/></svg>
              <div className="labimg-error-content">
                <p className="labimg-error-text">{imageError}</p>
                <button className="labimg-error-retry" onClick={() => setImageError('')}>닫기</button>
              </div>
            </div>
          )}

          {/* 생성 결과 */}
          {generatedImage && !isGeneratingImage && (
            <div className="labimg-result">
              <div className="labimg-result-card">
                <img 
                  src={generatedImage} 
                  alt="AI가 생성한 이미지" 
                  className="labimg-result-img"
                />
              </div>
              <div className="labimg-result-actions">
                <button 
                  className="labimg-download-btn"
                  onClick={handleImageDownload}
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M15.75 11.25v3a1.5 1.5 0 01-1.5 1.5h-10.5a1.5 1.5 0 01-1.5-1.5v-3M5.25 7.5L9 11.25l3.75-3.75M9 11.25V2.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  다운로드
                </button>
                <button 
                  className="labimg-retry-btn"
                  onClick={handleImageGenerate}
                >
                  다시 만들기
                </button>
              </div>
            </div>
          )}
        </div>
        
        <LoginPrompt
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          feature="lab"
        />
        
        <ProUpgradeModal
          isOpen={showProRequiredModal}
          onClose={() => setShowProRequiredModal(false)}
          feature="이미지 생성"
        />
      </>
    );
  }

  return null;
};

export default LabPage;
